import React, { useState, useCallback, useMemo } from 'react'
import { Button, Grid, Typography, Box } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { useProperty, useRequiredProperty, useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { TSPayLink2PayDialog } from './link2pay-dialog'
import { Link2PayOutput, useCreateLink2PayPosOnlineMutation, useCreateLinkFroReservationMutation } from 'src/api/api.generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareUpRight } from '@fortawesome/free-solid-svg-icons'
import { faCreditCard } from '@fortawesome/free-regular-svg-icons'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

export type TSPayLink2PayProps = {
  propertyId: string
  cashDeskTypeId: string
  amount?: number | null
  paymentDescripton: string
  reservationId?: string
  customerId?: string
  customerEmail?: string
  onLoading: (loading: boolean) => void
  onPaymentConfirmed: (success: boolean) => void
}

export const TSPayLink2Pay = (props: TSPayLink2PayProps) => {
  const { propertyId, cashDeskTypeId, amount, paymentDescripton, reservationId, customerId, customerEmail, onLoading, onPaymentConfirmed } = props
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const { id: tenantId, currency } = useTenant()
  const { fiscalConfigurations } = useRequiredProperty()
  const showError = useErrorSnackBar()

  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false)

  const [createPayReservationLink, { loading }] = useCreateLinkFroReservationMutation()
  const [createPayLink] = useCreateLink2PayPosOnlineMutation()
  const [link2Pay, setLink2Pay] = useState<Link2PayOutput>()

  const merchantInfo = useMemo(() => {
    return fiscalConfigurations?.find((c) => c.cashDeskTypeIds.includes(cashDeskTypeId))?.tspay?.merchantRef
  }, [cashDeskTypeId, fiscalConfigurations])

  const generateLink2Pay = useCallback(async () => {
    // Se il pagamento può essere legato ad una prenotazione genera il link utilizzando createLinkForReservation altrimenti utilizza createLink2PayPosOnline
    if (amount) {
      onLoading(true)

      // Pagamento relativo ad una prenotazione
      if (reservationId) {
        const result = await createPayReservationLink({
          variables: {
            tenantId: tenantId,
            propertyId: propertyId,
            reservationId: reservationId,
            payment: {
              amount: amount,
              currency: currency,
              description: paymentDescripton,
            },
          },
        })

        if (result.data?.createLinkForReservation) setLink2Pay(result.data.createLinkForReservation)
        else if (result.errors) showError(result.errors)
      } else {
        // Pagamento generico
        if (customerId && merchantInfo) {
          const result = await createPayLink({
            variables: {
              input: {
                tenantId: tenantId,
                propertyId: propertyId,
                amount: amount,
                currency: currency,
                customerId: customerId,
                description: paymentDescripton,
                title: 'TODO: Titolo del pagamento',
                paymentRef: paymentDescripton,
                merchantRef: merchantInfo,
              },
            },
          })

          if (result.data?.createLink2PayPosOnLine) setLink2Pay(result.data.createLink2PayPosOnLine)
          else if (result.errors) showError(result.errors)
        }
      }

      onLoading(false)
    }
  }, [reservationId, customerId, amount, merchantInfo])

  const handleClose = (event: Record<string, unknown>, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      console.log(reason)
    } else {
      setDialogIsOpen(false)
    }
  }

  return (
    <>
      {amount && (reservationId || (customerId && merchantInfo)) ? (
        <Grid container spacing={2} width="100%">
          <Grid item xs={12}>
            <Typography variant="caption">{t('pms.payment.tspay.link2pay.summary', { amount: formatCurrency(amount, currency) })}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" width={'100%'}>
              <Button
                disabled={loading}
                variant="outlined"
                onClick={() => {
                  setDialogIsOpen(true)
                  generateLink2Pay()
                }}
              >
                {t('pms.payment.tspay.link2pay.button_start')} <FontAwesomeIcon icon={faCreditCard} style={{ paddingLeft: 8 }} />
              </Button>
              <Button variant="text">
                {t('pms.payment.tspay.link2pay.button_goto_tspay')}
                <FontAwesomeIcon icon={faSquareUpRight} style={{ paddingLeft: 8 }} />
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}></Grid>
        </Grid>
      ) : undefined}
      {link2Pay && (
        <TSPayLink2PayDialog
          open={dialogIsOpen}
          onClose={handleClose}
          link2Pay={link2Pay}
          defaultEmail={customerEmail ?? ''}
          onPaymentConfirmed={(success) => {
            setDialogIsOpen(false)
            onPaymentConfirmed(success)
          }}
        />
      )}
    </>
  )
}
